/**
 * Exam selector HTML builder and fetcher for Storefront API.
 * Fetches exam_category_group metaobjects, builds HTML, injects and initializes behavior.
 * Exam names and redirect URLs are sourced from exam_landing_pages field (List of Links).
 */
(function (global) {
  "use strict";

  const MODAL_ID = "exam-selector-modal";
  const TEXT_ID = "exam-selector-text";
  const ICON_ID = "exam-selector-icon";
  const DEFAULT_TEXT = "Select Exam";
  const CACHE_KEY = "examSelectorCategoryGroups";

  const PREFERENCES_KEY = "preferences";
  const ROOT_SELECTOR = "[data-exam-selector-root]";
  const INIT_FLAG = "data-exam-selector-initialized";

  function getCachedExamData() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.categoryGroups) && parsed.categoryGroups.length > 0) {
        return parsed;
      }
    } catch (e) {}
    return null;
  }

  function setCachedExamData(normalized) {
    try {
      if (normalized && Array.isArray(normalized.categoryGroups)) {
        localStorage.setItem(CACHE_KEY, JSON.stringify(normalized));
      }
    } catch (e) {}
  }

  function getPreferences() {
    try {
      const raw = localStorage.getItem(PREFERENCES_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {}
    return null;
  }

  function setPreferences(prefs) {
    try {
      if (prefs && typeof prefs === "object") {
        localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
      }
    } catch (e) {}
  }

  function getField(node, key) {
    if (!node || !node.fields || !Array.isArray(node.fields)) return null;
    for (let i = 0; i < node.fields.length; i++) {
      if (node.fields[i].key === key) return node.fields[i];
    }
    return null;
  }

  function fieldValue(field) {
    return field && field.value != null ? String(field.value).trim() : "";
  }

  function parseJsonArray(val) {
    if (val == null || val === "") return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [val];
    } catch (e) {
      return val ? [val] : [];
    }
  }

  function slugify(name) {
    if (name == null) return "";
    return String(name)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  /**
   * Normalize Storefront API metaobject nodes into category groups for building HTML.
   * Exam names and redirect URLs are sourced from exam_landing_pages field (List of Links).
   * @param {Object} data - { metaobjects: { nodes } }
   * @returns {{ categoryGroups: Array }}
   */
  function normalizeExamData(data) {
    const nodes = (data && data.metaobjects && data.metaobjects.nodes) || [];

    const categoryGroups = [];
    for (let n = 0; n < nodes.length; n++) {
      const node = nodes[n];
      const categoryName = fieldValue(getField(node, "category_name"));
      const displayOrderField = getField(node, "display_order");
      let displayOrder = 0;
      if (displayOrderField && displayOrderField.value != null) {
        const num = parseInt(displayOrderField.value, 10);
        if (!isNaN(num)) displayOrder = num;
      }

      const examLandingPagesField = getField(node, "exam_landing_pages");
      let examLandingPages = [];
      if (examLandingPagesField && examLandingPagesField.value) {
        try {
          const parsedValue = JSON.parse(examLandingPagesField.value);
          if (Array.isArray(parsedValue)) {
            examLandingPages = parsedValue;
          }
        } catch (e) {
          examLandingPages = [];
        }
      }

      const examIconsField = getField(node, "exam_icons");
      const iconUrls = [];
      if (examIconsField && examIconsField.references && examIconsField.references.nodes) {
        for (let i = 0; i < examIconsField.references.nodes.length; i++) {
          const media = examIconsField.references.nodes[i];
          const url = media && media.image && media.image.url ? media.image.url : null;
          if (url) iconUrls.push(url);
        }
      }

      const bgColorsField = getField(node, "exam_background_color");
      const bgColors = bgColorsField ? parseJsonArray(bgColorsField.value) : [];

      const defaultIconField = getField(node, "default_exam_icons");
      let defaultIconUrl = null;
      if (defaultIconField && defaultIconField.reference) {
        const ref = defaultIconField.reference;
        if (ref && ref.image && ref.image.url) defaultIconUrl = ref.image.url;
      }

      const exams = [];
      for (let e = 0; e < examLandingPages.length; e++) {
        const landingPage = examLandingPages[e];
        if (!landingPage || typeof landingPage !== "object") continue;
        
        const examName = landingPage.text != null ? String(landingPage.text).trim() : "";
        let redirectUrl = landingPage.url != null ? String(landingPage.url).trim() : "/";
        
        if (!examName) continue;
        
        if (!redirectUrl || redirectUrl === "") redirectUrl = "/";
        
        const examSlug = slugify(examName);
        const iconUrl = (iconUrls.length > 0 && iconUrls[e] != null ? iconUrls[e] : defaultIconUrl) || "";
        const bgColor = bgColors[e] != null ? String(bgColors[e]).trim() : "";
        exams.push({
          examName: examName,
          examSlug: examSlug,
          redirectUrl: redirectUrl,
          iconUrl: iconUrl,
          bgColor: bgColor,
        });
      }

      if (categoryName || exams.length > 0) {
        categoryGroups.push({
          categoryName: categoryName,
          displayOrder: displayOrder,
          exams: exams,
        });
      }
    }

    categoryGroups.sort(function (a, b) {
      return a.displayOrder - b.displayOrder;
    });

    return { categoryGroups: categoryGroups };
  }

  /**
   * Strip origin/query/hash and trailing slash so URLs from different sources
   * (absolute vs relative, with/without trailing slash) compare equal.
   */
  function normalizePath(url) {
    if (!url) return "";
    let path = String(url).trim();
    try {
      const a = global.document.createElement("a");
      a.href = path;
      path = a.pathname;
    } catch (e) {}
    path = path.split("?")[0].split("#")[0];
    if (path.length > 1 && path.charAt(path.length - 1) === "/") path = path.slice(0, -1);
    return path;
  }

  /**
   * Find the exam (if any) whose redirectUrl matches the given pathname exactly.
   * Returns null for any URL not in the known cohort list — this is what safely
   * distinguishes real cohort pages (e.g. /pages/iit-jee) from unrelated
   * /pages/* content (e.g. /pages/privacy-policy): exact match only, no prefix guessing.
   */
  function findExamForPath(categoryGroups, pathname) {
    const target = normalizePath(pathname);
    if (!target || target === "/" || target === "") return null;
    for (let g = 0; g < categoryGroups.length; g++) {
      const exams = categoryGroups[g].exams || [];
      for (let e = 0; e < exams.length; e++) {
        const exam = exams[e];
        if (exam.redirectUrl && normalizePath(exam.redirectUrl) === target) return exam;
      }
    }
    return null;
  }

  /**
   * If the current page is a known cohort's landing page (matched against the
   * exam_landing_pages data, same source that powers the header modal) but the
   * user got there by a direct URL rather than clicking a cohort card, sync
   * localStorage + the header trigger to match — mirroring the non-navigation
   * part of selectExam() below, minus analytics (this is an implicit sync, not
   * a deliberate user action) and minus the redirect (already on the page).
   */
  function syncSelectedCohortFromCurrentUrl(categoryGroups) {
    try {
      if (!categoryGroups || !categoryGroups.length) return;
      const pathname = (global.location && global.location.pathname) || "";
      if (pathname === "/") return; // homepage handled by home-cohort-redirect.js

      const match = findExamForPath(categoryGroups, pathname);
      if (!match || !match.examName) return;

      let currentName = "";
      try {
        currentName = (global.localStorage.getItem("selectedExamName") || "").trim();
      } catch (e) {}
      if (currentName && slugify(currentName) === match.examSlug) return; // already in sync

      try {
        global.localStorage.setItem("selectedExamName", match.examName);
        global.localStorage.setItem("selectedExamIcon", match.iconUrl || "");
        global.localStorage.setItem("selectedExamRedirectUrl", match.redirectUrl);

        const isLoggedIn = typeof global.hasCustomer !== "undefined" && global.hasCustomer === true;
        if (isLoggedIn) {
          global.sessionStorage.setItem("cohortChangedInSession", "true");
        } else {
          const existingPrefs = getPreferences() || {};
          setPreferences({
            exams: [String(match.examName).replace(/\s+/g, "-")],
            board: existingPrefs.board != null ? String(existingPrefs.board) : "",
            language: existingPrefs.language != null ? String(existingPrefs.language) : "",
            class: existingPrefs.class != null ? String(existingPrefs.class) : "",
            examIcon: String(match.iconUrl || ""),
            redirection_url: match.redirectUrl,
          });
        }
      } catch (e) {}

      try {
        global.dispatchEvent(
          new CustomEvent("examSelected", {
            detail: {
              examName: match.examName,
              examIcon: match.iconUrl,
              redirection_url: match.redirectUrl,
            },
          })
        );
      } catch (e) {}
    } catch (e) {}
  }

  /**
   * Build only the modal inner HTML (exam sections/cards). Trigger + dialog shell come from Liquid (exam-selector.liquid).
   * @param {Object} options - { categoryGroups, defaultIconUrl }
   * @returns {string}
   */
  function buildExamSelectorModalInnerHTML(options) {
    const categoryGroups = (options && options.categoryGroups) || [];
    const defaultIconUrl = (options && options.defaultIconUrl) || "";

    function escapeAttr(s) {
      if (s == null) return "";
      return String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    let sectionsHTML = "";
    for (let g = 0; g < categoryGroups.length; g++) {
      const group = categoryGroups[g];
      const exams = group.exams || [];
      if (exams.length === 0) continue;

      const listClass = group.displayOrder >= 2 ? " exam-selector-modal__list" : "";
      const cardClass = group.displayOrder >= 2 ? " category-card" : "";
      let cardsHTML = "";
      for (let c = 0; c < exams.length; c++) {
        const exam = exams[c];
        if (!exam.examName) continue;
        const iconUrl = exam.iconUrl || defaultIconUrl;
        const styleAttr = exam.bgColor ? ` style="background: ${escapeAttr(exam.bgColor)}"` : "";
        cardsHTML += `
          <button type="button" class="exam-selector-modal__card exam-card${cardClass}"
            data-exam-name="${escapeAttr(exam.examName)}"
            data-exam-icon="${escapeAttr(iconUrl)}"
            data-exam-slug="${escapeAttr(exam.examSlug)}"
            data-redirect-url="${escapeAttr(exam.redirectUrl)}"${styleAttr}>
            <img src="${escapeAttr(iconUrl)}" alt="${escapeAttr(exam.examName)}"
              class="exam-card__icon" loading="lazy" width="48" height="48">
            <span class="exam-card__name">${escapeAttr(exam.examName)}</span>
          </button>`;
      }
      sectionsHTML += `
        <div class="exam-selector-modal__section exam-category-group">
          <p class="exam-selector-modal__section-title">${escapeAttr(group.categoryName)}</p>
          <div class="exam-selector-modal__grid${listClass}">${cardsHTML}</div>
        </div>`;
    }
    return sectionsHTML;
  }

  /**
   * Fetch exam selector data from Storefront API.
   * @param {string} token - Storefront access token
   * @param {string} query - GraphQL query string
   * @returns {Promise<{ categoryGroups: Array }>}
   */
  function fetchExamSelectorData(token, query) {
    if (!token || !query) return Promise.resolve({ categoryGroups: [] });
    return fetch("/api/2025-01/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query: query }),
    })
      .then(function (res) {
        if (!res.ok)
          return res.text().then(function (t) {
            throw new Error(`${res.status} ${t.slice(0, 200)}`);
          });
        return res.json();
      })
      .then(function (result) {
        if (result.errors) return { categoryGroups: [] };
        const data = result.data || {};
        return normalizeExamData(data);
      })
      .catch(function () {
        return { categoryGroups: [] };
      });
  }

  /**
   * Initialize exam selector behavior (update display, selectExam, card click, drag handle).
   * Call after injecting HTML. Uses modalId for dialog and TEXT_ID/ICON_ID for trigger.
   */
  function initExamSelectorBehavior(root) {
    const behaviorRoot =
      root && root.querySelector
        ? root
        : global.document && global.document.querySelector(ROOT_SELECTOR);
    if (!behaviorRoot) return;
    if (behaviorRoot.getAttribute("data-pw-exam-behavior-bound") === "true") {
      return;
    }
    behaviorRoot.setAttribute("data-pw-exam-behavior-bound", "true");

    function updateExamDisplay(name, icon) {
      const textEl = document.getElementById(TEXT_ID);
      const iconEl = document.getElementById(ICON_ID);
      if (textEl && name) textEl.textContent = name;
      if (iconEl && icon) {
        iconEl.src = icon;
        iconEl.alt = `${name} Icon`;
      }
    }

    function closeModal() {
      const dialogComponent = document.getElementById(MODAL_ID);
      if (!dialogComponent) return;
      const dialog = dialogComponent.querySelector("dialog");
      if (dialog && dialog.open) {
        if (typeof dialogComponent.closeDialog === "function") dialogComponent.closeDialog();
        else if (dialog.close) dialog.close();
        else dialog.dispatchEvent(new Event("close", { bubbles: true }));
      }
    }

    function selectExam(element) {
      if (!element) return;
      const examName = element.getAttribute("data-exam-name");
      const examIcon = element.getAttribute("data-exam-icon");
      const examSlug = element.getAttribute("data-exam-slug");

      const selectDedupeKey = String(examName || "") + "|" + (global.location ? global.location.pathname : "");
      const selectNow = Date.now();
      if (!global.__PW_EXAM_SELECT_LAST__) {
        global.__PW_EXAM_SELECT_LAST__ = { key: "", ts: 0 };
      }
      if (
        global.__PW_EXAM_SELECT_LAST__.key === selectDedupeKey &&
        selectNow - global.__PW_EXAM_SELECT_LAST__.ts < 3000
      ) {
        return;
      }
      global.__PW_EXAM_SELECT_LAST__ = { key: selectDedupeKey, ts: selectNow };
      const isPlaceholderExam = function (value) {
        const v = String(value || "").trim();
        return !v || v === "Select Exam" || v === "Select Exam/Class";
      };
      const textEl = document.getElementById(TEXT_ID);
      const previousExamText = textEl ? String(textEl.textContent || "").trim() : "";
      let previousExamFromStorage = "";
      try {
        previousExamFromStorage = localStorage.getItem("selectedExamName") || "";
      } catch (e) {}
      let previousExamName = null;
      if (!isPlaceholderExam(previousExamText)) {
        previousExamName = previousExamText;
      } else if (!isPlaceholderExam(previousExamFromStorage)) {
        previousExamName = previousExamFromStorage.trim();
      } else {
        try {
          const customerCohort = window.__CUSTOMER_DATA__ && window.__CUSTOMER_DATA__.cohort_name;
          if (!isPlaceholderExam(customerCohort)) previousExamName = String(customerCohort).trim();
        } catch (e) {}
      }

      if (!examName || !examIcon) return;

      let redirectUrl = (element.getAttribute("data-redirect-url") || "").trim() || "/";
      if (!redirectUrl) redirectUrl = "/";

      if (typeof window.trackCohortChangeSubmit === "function") {
        try {
          window.trackCohortChangeSubmit({
            cohort_name: examName,
            exam_name: examName,
            exam_slug: examSlug,
            previous_cohort_name: previousExamName,
          });
        } catch (e) {}
      }

      try {
        localStorage.setItem("selectedExamName", examName);
        localStorage.setItem("selectedExamIcon", examIcon);
        localStorage.setItem("selectedExamRedirectUrl", redirectUrl);
        const isLoggedIn = typeof window.hasCustomer !== "undefined" && window.hasCustomer === true;
        if (isLoggedIn) {
          // Set session flag to indicate cohort was changed in this session
          try {
            sessionStorage.setItem("cohortChangedInSession", "true");
          } catch (e) {}
        } else {
          const existingPrefs = getPreferences() || {};
          const prefs = {
            exams: [String(examName).replace(/\s+/g, "-")],
            board: (existingPrefs.board != null && existingPrefs.board !== undefined) ? String(existingPrefs.board) : "",
            language: (existingPrefs.language != null && existingPrefs.language !== undefined) ? String(existingPrefs.language) : "",
            class: (existingPrefs.class != null && existingPrefs.class !== undefined) ? String(existingPrefs.class) : "",
            examIcon: String(examIcon || ""),
            redirection_url: redirectUrl,
          };
          setPreferences(prefs);
        }
      } catch (e) {}

      try {
        window.dispatchEvent(new CustomEvent("examSelected", {
          detail: {
            examName: examName,
            examIcon: examIcon,
            redirection_url: redirectUrl,
          },
        }));
      } catch (e) {}

      updateExamDisplay(examName, examIcon);
      setTimeout(closeModal, 100);

      if (redirectUrl)
        setTimeout(function () {
          window.location.href = redirectUrl;
        }, 150);
    }

    function handleCardClick(e) {
      const card = e.target.closest(".exam-selector-modal__card");
      if (card) {
        e.preventDefault();
        e.stopPropagation();
        selectExam(card);
      }
    }

    function restoreSavedExam() {
      // FIX: Try cached event reference first, fall back to window global.
      // Global assignment is no longer done (causes Shopify renderer freeze),
      // so the event listener below is the primary path.
      if (typeof window.getSavedExamForDisplay === "function") {
        const { savedName, savedIcon } = window.getSavedExamForDisplay();
        if (savedName || savedIcon) updateExamDisplay(savedName, savedIcon);
      }
    }

    // Listen for examDisplayReady event (fired by exam-display-utils.js defer script).
    // This handles the case where this defer script runs before exam-display-utils.js.
    window.addEventListener("examDisplayReady", function(ev) {
      var fn = ev.detail && ev.detail.getSavedExamForDisplay;
      if (typeof fn !== "function") return;
      var result = fn();
      if (result.savedName || result.savedIcon) updateExamDisplay(result.savedName, result.savedIcon);
    });

    function initializeDragHandle() {
      const dialogComponent = document.getElementById(MODAL_ID);
      if (!dialogComponent) return;
      const dialog = dialogComponent.querySelector(".exam-selector-modal__content");
      const dragHandle = dialogComponent.querySelector(".exam-selector-modal__drag-handle");
      if (!dialog || !dragHandle || window.innerWidth > 749) return;

      let startY = 0;
      let startHeight = 0;
      let isDragging = false;
      const minH = 50;
      const maxH = 90;

      function handleStart(e) {
        isDragging = true;
        startY = e.touches ? e.touches[0].clientY : e.clientY;
        startHeight = parseFloat(dialog.style.height) || 50;
        dialog.style.transition = "none";
        document.body.style.userSelect = "none";
        e.preventDefault();
        e.stopPropagation();
      }
      function handleMove(e) {
        if (!isDragging) return;
        const currentY = e.touches ? e.touches[0].clientY : e.clientY;
        const deltaY = startY - currentY;
        const newHeight = Math.max(minH, Math.min(maxH, startHeight + (deltaY / window.innerHeight) * 100));
        dialog.style.height = `${newHeight}vh`;
        e.preventDefault();
        e.stopPropagation();
      }
      function handleEnd() {
        if (!isDragging) return;
        isDragging = false;
        dialog.style.transition = "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
        document.body.style.userSelect = "";
        const currentHeight = parseFloat(dialog.style.height);
        if (currentHeight < 60) dialog.style.height = `${minH}vh`;
        else if (currentHeight > 80) dialog.style.height = `${maxH}vh`;
      }

      const newDragHandle = dragHandle.cloneNode(true);
      dragHandle.parentNode.replaceChild(newDragHandle, dragHandle);
      newDragHandle.addEventListener("touchstart", handleStart, { passive: false });
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);
      newDragHandle.addEventListener("mousedown", handleStart);
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
    }

    restoreSavedExam();

    const modalInner = behaviorRoot.querySelector(".exam-selector-modal__inner");
    if (modalInner && modalInner.getAttribute("data-pw-exam-click-bound") !== "true") {
      modalInner.setAttribute("data-pw-exam-click-bound", "true");
      modalInner.addEventListener("click", handleCardClick);
    }

    const dialogComponent = document.getElementById(MODAL_ID);
    if (dialogComponent) {
      const dialog = dialogComponent.querySelector("dialog");
      if (dialog) {
        dialog.addEventListener("open", function () {
          setTimeout(initializeDragHandle, 100);
        });
        const observer = new MutationObserver(function () {
          if (dialog.hasAttribute("open")) setTimeout(initializeDragHandle, 100);
        });
        observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
      }
    }
  }

  /**
   * Fetch exam data and inject into modal inner. Expects trigger + dialog shell from Liquid (exam-selector.liquid).
   * @param {HTMLElement} container - Element containing .exam-selector-modal__inner (from Liquid)
   * @param {Object} options - { token, query, defaultIconUrl, defaultText }
   */
  function fetchAndInjectExamSelector(container, options) {
    if (!container) return;
    const token = (options && options.token) || "";
    const query = (options && options.query) || "";
    const defaultIconUrl = (options && options.defaultIconUrl) || "";
    const modalInner = container.querySelector(".exam-selector-modal__inner");
    if (!modalInner) return;

    initExamSelectorBehavior(container);

    const cached = getCachedExamData();
    if (cached) {
      modalInner.innerHTML = buildExamSelectorModalInnerHTML({
        categoryGroups: cached.categoryGroups,
        defaultIconUrl: defaultIconUrl,
      });
      syncSelectedCohortFromCurrentUrl(cached.categoryGroups);
      return;
    }

    fetchExamSelectorData(token, query).then(function (normalized) {
      if (normalized.categoryGroups && normalized.categoryGroups.length > 0) {
        setCachedExamData(normalized);
      }
      modalInner.innerHTML = buildExamSelectorModalInnerHTML({
        categoryGroups: normalized.categoryGroups,
        defaultIconUrl: defaultIconUrl,
      });
      syncSelectedCohortFromCurrentUrl(normalized.categoryGroups);
    });
  }

  function decodeAttr(val) {
    if (val == null) return "";
    // Attributes are HTML-escaped in Liquid; browser returns decoded string via getAttribute
    return String(val);
  }

  function getOptionsFromRoot(root) {
    if (!root || !root.getAttribute) return null;
    const token = (typeof window !== "undefined" && window.PW_STOREFRONT_TOKEN) || "";
    const query = decodeAttr(root.getAttribute("data-exam-selector-query") || "");
    const defaultIconUrl = decodeAttr(root.getAttribute("data-default-icon-url") || "");
    const defaultText = decodeAttr(root.getAttribute("data-default-text") || DEFAULT_TEXT);
    if (!token || !query) return null;
    return { token, query, defaultIconUrl, defaultText };
  }

  function shouldHydrateRoot(root) {
    const modalInner = root.querySelector && root.querySelector(".exam-selector-modal__inner");
    if (!modalInner) return false;

    // If markup is empty (common after morphSection), we must hydrate again.
    if (modalInner.childElementCount === 0 && (modalInner.textContent || "").trim() === "") return true;

    // If we never initialized this root, hydrate once.
    if (!root.hasAttribute(INIT_FLAG)) return true;

    return false;
  }

  function initAllExamSelectors() {
    if (!global.document || !global.document.querySelectorAll) return;
    const roots = global.document.querySelectorAll(ROOT_SELECTOR);
    for (let i = 0; i < roots.length; i++) {
      const root = roots[i];
      if (!root) continue;
      if (!shouldHydrateRoot(root)) continue;

      const options = getOptionsFromRoot(root);
      if (!options) continue;

      try {
        root.setAttribute(INIT_FLAG, "true");
      } catch (e) {}

      fetchAndInjectExamSelector(root, options);
    }
  }

  function scheduleInitAll() {
    // Defer to let morphSection finish updating DOM.
    try {
      Promise.resolve().then(initAllExamSelectors);
    } catch (e) {
      setTimeout(initAllExamSelectors, 0);
    }
  }

  global.ExamSelectorStorefront = {
    normalizeExamData: normalizeExamData,
    buildExamSelectorModalInnerHTML: buildExamSelectorModalInnerHTML,
    fetchExamSelectorData: fetchExamSelectorData,
    fetchAndInjectExamSelector: fetchAndInjectExamSelector,
    initExamSelectorBehavior: initExamSelectorBehavior,
    initAllExamSelectors: initAllExamSelectors,
    findExamForPath: findExamForPath,
    syncSelectedCohortFromCurrentUrl: syncSelectedCohortFromCurrentUrl,
  };

  // Auto-init on initial load
  if (global.document) {
    if (global.document.readyState === "loading") {
      global.document.addEventListener("DOMContentLoaded", scheduleInitAll);
    } else {
      scheduleInitAll();
    }

    // Shopify theme editor section reloads
    global.document.addEventListener("shopify:section:load", scheduleInitAll);

    // Cart updates often morph header/sections; scripts in morphed HTML won't run, so rehydrate.
    global.document.addEventListener("cart:update", scheduleInitAll);

    // Fallback: DOM replacements (e.g. morphSection of header)
    try {
      const mo = new MutationObserver(function () {
        scheduleInitAll();
      });
      mo.observe(global.document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }
})(typeof window !== "undefined" ? window : this);
